import Joi from 'joi';
import type { CustomSuitabilityCheckerProps, Questions } from './models';

export type SelectedAnswer = {
  answer: string;
  isSuitable: boolean;
};

type SuitabilityCheckerModelData = CustomSuitabilityCheckerProps['modelData'];

const answerSchema = Joi.object({
  answer: Joi.string().required(),
  is_suitable: Joi.boolean().required(),
})
  .unknown(true)
  .required();

const questionSchema = Joi.object({
  question: Joi.string().required(),
  multiple_choice: Joi.boolean().required(),
  answers: Joi.array().items(answerSchema).min(1).required(),
})
  .unknown(true)
  .required();

const popupImageSchema = Joi.object({
  url: Joi.string().required(),
  title: Joi.string().allow(null, ''),
  filename: Joi.string().allow(null, ''),
})
  .unknown(true)
  .allow(null);

const suitabilityCheckerModelDataSchema = Joi.object({
  heading: Joi.string().allow(null, ''),
  title: Joi.string().allow('', null),
  questions: Joi.array().items(questionSchema).min(1).required(),
  submit_button_label: Joi.string().required(),
  submit_error_message: Joi.string().allow(null, ''),
  download_button_label: Joi.string().required(),
  positive_result_message: Joi.string().required(),
  negative_result_message: Joi.string().required(),
  disclaimer: Joi.string().allow(null, ''),
  popup_image: popupImageSchema,
})
  .unknown(true)
  .required();

export const validateSuitabilityCheckerModelData = (
  modelData: unknown
): SuitabilityCheckerModelData => {
  const { error, value } = suitabilityCheckerModelDataSchema.validate(modelData, {
    abortEarly: false,
  });

  if (error) {
    throw new Error(`SuitabilityChecker validation error: ${error.message}`);
  }

  const normalizedHeading = value.heading || value.title;
  if (!normalizedHeading) {
    throw new Error('SuitabilityChecker validation error: "heading" or "title" must be provided');
  }

  return {
    ...(value as CustomSuitabilityCheckerProps['modelData']),
    heading: normalizedHeading,
    submit_error_message:
      value.submit_error_message || 'Please answer all questions before submitting.',
  };
};

export const updateSingleChoiceAnswers = (
  previous: Record<string, SelectedAnswer[]>,
  questionKey: string,
  answer: string,
  isSuitable: boolean
): Record<string, SelectedAnswer[]> => ({
  ...previous,
  [questionKey]: [{ answer, isSuitable }],
});

export const updateMultipleChoiceAnswers = (
  previous: Record<string, SelectedAnswer[]>,
  questionKey: string,
  answer: string,
  isSuitable: boolean,
  checked: boolean
): Record<string, SelectedAnswer[]> => {
  const currentAnswers = previous[questionKey] ?? [];

  if (checked) {
    if (currentAnswers.some((selected) => selected.answer === answer)) {
      return previous;
    }

    if (isSuitable) {
      return {
        ...previous,
        [questionKey]: [{ answer, isSuitable }],
      };
    }

    return {
      ...previous,
      [questionKey]: [
        ...currentAnswers.filter((selected) => !selected.isSuitable),
        { answer, isSuitable },
      ],
    };
  }

  return {
    ...previous,
    [questionKey]: currentAnswers.filter((selected) => selected.answer !== answer),
  };
};

export const getSubmitResultMessage = (
  selectedAnswersByQuestion: Record<string, SelectedAnswer[]>,
  positiveResultMessage: string,
  negativeResultMessage: string
): string => {
  const selectedAnswers = Object.values(selectedAnswersByQuestion).flat();
  const hasUnsuitableAnswer = selectedAnswers.some((selected) => !selected.isSuitable);

  return hasUnsuitableAnswer ? negativeResultMessage : positiveResultMessage;
};

export const areAllQuestionsAnswered = (
  questions: Questions[],
  selectedAnswersByQuestion: Record<string, SelectedAnswer[]>
): boolean =>
  questions.every((question) => {
    const selectedAnswers = selectedAnswersByQuestion[question.question] ?? [];
    return selectedAnswers.length > 0;
  });

type DownloadSuitabilitySummaryImageParams = {
  heading: string;
  questions: Questions[];
  selectedAnswersByQuestion: Record<string, SelectedAnswer[]>;
  submitResultMessage: string | null;
  disclaimer?: string | null;
  popupImage?: { url: string } | null;
};
// TODO: Align with designs
export const downloadSuitabilitySummaryImage = async ({
  heading,
  questions,
  selectedAnswersByQuestion,
  submitResultMessage,
  disclaimer,
  popupImage,
}: DownloadSuitabilitySummaryImageParams): Promise<void> => {
  const PADDING = 40;
  const WIDTH = 800;
  const FONT_TITLE = 'bold 22px sans-serif';
  const FONT_QUESTION = 'bold 16px sans-serif';
  const FONT_ANSWER = '15px sans-serif';
  const LINE_HEIGHT_QUESTION = 28;
  const LINE_HEIGHT_ANSWER = 22;
  const ANSWER_INDENT = 32;
  const SECTION_GAP = 24;
  const PRIMARY_COLOR = '#001489';

  const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const plainText = text
      .replace(/<li[^>]*>/gi, '\n')
      .replace(/<\/li>/gi, '')
      .replace(/<[^>]*>/g, ' ')
      .trim();
    const lines: string[] = [];

    plainText.split('\n').forEach((paragraph) => {
      const words = paragraph.trim().replace(/\s+/g, ' ').split(' ').filter(Boolean);
      let current = '';

      for (const word of words) {
        const test = current ? `${current} ${word}` : word;
        if (ctx.measureText(test).width > maxWidth && current) {
          lines.push(current);
          current = word;
        } else {
          current = test;
        }
      }
      if (current) {
        lines.push(current);
      }
    });

    return lines;
  };

  const offscreen = document.createElement('canvas');
  const offCtx = offscreen.getContext('2d');
  if (!offCtx) {
    return;
  }

  const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  let logoImage: HTMLImageElement | null = null;
  const logoUrl = `${window.location.origin}/files/durex-logo.png`;
  try {
    logoImage = await loadImage(logoUrl);
  } catch (e) {
    logoImage = null;
  }

  let packshotImage: HTMLImageElement | null = null;
  if (popupImage?.url) {
    try {
      packshotImage = await loadImage(popupImage.url);
    } catch (e) {
      packshotImage = null;
    }
  }

  const contentWidth = WIDTH - PADDING * 2;
  let totalHeight = PADDING * 2;
  let logoHeight = 0;

  if (logoImage) {
    const logoMaxW = Math.min(180, WIDTH - PADDING * 2);
    logoHeight = (logoImage.naturalHeight / logoImage.naturalWidth) * logoMaxW;
    totalHeight += logoHeight + SECTION_GAP;
  }

  offCtx.font = FONT_TITLE;
  totalHeight +=
    wrapText(offCtx, heading, contentWidth).length * LINE_HEIGHT_QUESTION + SECTION_GAP;

  if (submitResultMessage) {
    offCtx.font = FONT_ANSWER;
    totalHeight +=
      wrapText(offCtx, submitResultMessage, contentWidth).length * LINE_HEIGHT_ANSWER + SECTION_GAP;
  }

  questions.forEach((question, index) => {
    offCtx.font = FONT_QUESTION;
    const questionNumber = `${index + 1}. `;
    const questionIndent = offCtx.measureText(questionNumber).width;
    totalHeight +=
      wrapText(offCtx, question.question, contentWidth - questionIndent).length *
      LINE_HEIGHT_QUESTION;

    const selectedAnswers = selectedAnswersByQuestion[question.question] ?? [];
    selectedAnswers.forEach((selectedAnswer) => {
      offCtx.font = FONT_ANSWER;
      totalHeight +=
        wrapText(offCtx, `• ${selectedAnswer.answer}`, contentWidth - ANSWER_INDENT).length *
        LINE_HEIGHT_ANSWER;
    });

    totalHeight += SECTION_GAP;
  });

  if (disclaimer) {
    offCtx.font = FONT_ANSWER;
    totalHeight +=
      wrapText(offCtx, disclaimer, contentWidth).length * LINE_HEIGHT_ANSWER + SECTION_GAP;
  }

  let packshotHeight = 0;
  if (packshotImage) {
    const packshotMaxW = contentWidth * 0.5;
    packshotHeight = (packshotImage.naturalHeight / packshotImage.naturalWidth) * packshotMaxW;
    totalHeight += packshotHeight + SECTION_GAP;
  }

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = totalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    return;
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, WIDTH, totalHeight);

  const now = new Date();
  const dateStamp = `${String(now.getDate()).padStart(2, '0')}/${String(
    now.getMonth() + 1
  ).padStart(2, '0')}/${now.getFullYear()}`;
  const FONT_DATE = '13px sans-serif';
  ctx.font = FONT_DATE;
  ctx.fillStyle = '#666666';
  const dateWidth = ctx.measureText(dateStamp).width;
  ctx.fillText(dateStamp, WIDTH - PADDING - dateWidth, PADDING);

  let y = PADDING;

  ctx.fillStyle = PRIMARY_COLOR;
  ctx.font = FONT_TITLE;
  const titleLines = wrapText(ctx, heading, contentWidth);

  if (logoImage) {
    const logoMaxW = Math.min(180, WIDTH - PADDING * 2);
    const logoW = logoMaxW;
    const logoH = (logoImage.naturalHeight / logoImage.naturalWidth) * logoW;
    const logoX = PADDING;
    const logoY = PADDING;
    ctx.drawImage(logoImage, logoX, logoY, logoW, logoH);
    y = Math.max(y, logoY + logoH + SECTION_GAP);
  }

  titleLines.forEach((line) => {
    ctx.fillStyle = PRIMARY_COLOR;
    ctx.fillText(line, PADDING, y + LINE_HEIGHT_QUESTION);
    y += LINE_HEIGHT_QUESTION;
  });
  y += SECTION_GAP;

  if (submitResultMessage) {
    ctx.fillStyle = PRIMARY_COLOR;
    ctx.font = FONT_ANSWER;
    const resultLines = wrapText(ctx, submitResultMessage, contentWidth);
    resultLines.forEach((line) => {
      ctx.fillText(line, PADDING, y + LINE_HEIGHT_ANSWER);
      y += LINE_HEIGHT_ANSWER;
    });
    y += SECTION_GAP;
  }

  questions.forEach((question, index) => {
    ctx.fillStyle = PRIMARY_COLOR;
    ctx.font = FONT_QUESTION;
    const questionNumber = `${index + 1}. `;
    const questionIndent = ctx.measureText(questionNumber).width;
    const questionLines = wrapText(ctx, question.question, contentWidth - questionIndent);
    questionLines.forEach((line, lineIndex) => {
      ctx.fillText(lineIndex === 0 ? `${questionNumber}${line}` : line, lineIndex === 0 ? PADDING : PADDING + questionIndent, y + LINE_HEIGHT_QUESTION);
      y += LINE_HEIGHT_QUESTION;
    });

    const selectedAnswers = selectedAnswersByQuestion[question.question] ?? [];
    ctx.fillStyle = PRIMARY_COLOR;
    ctx.font = FONT_ANSWER;
    selectedAnswers.forEach((selectedAnswer) => {
      const answerLines = wrapText(ctx, `• ${selectedAnswer.answer}`, contentWidth - ANSWER_INDENT);
      answerLines.forEach((line) => {
        ctx.fillText(line, PADDING + ANSWER_INDENT, y + LINE_HEIGHT_ANSWER);
        y += LINE_HEIGHT_ANSWER;
      });
    });

    y += SECTION_GAP;
  });

  if (disclaimer) {
    ctx.fillStyle = PRIMARY_COLOR;
    ctx.font = FONT_ANSWER;
    const disclaimerLines = wrapText(ctx, disclaimer, contentWidth);
    disclaimerLines.forEach((line) => {
      ctx.fillText(line, PADDING, y + LINE_HEIGHT_ANSWER);
      y += LINE_HEIGHT_ANSWER;
    });
    y += SECTION_GAP;
  }

  if (packshotImage) {
    const packshotW = Math.min(contentWidth, packshotImage.naturalWidth) * 0.5;
    const packshotH = (packshotImage.naturalHeight / packshotImage.naturalWidth) * packshotW;
    const packshotX = (WIDTH - packshotW) / 2;
    const packshotY = y;
    ctx.drawImage(packshotImage, packshotX, packshotY, packshotW, packshotH);
    y += packshotH + SECTION_GAP;
  }

  const link = document.createElement('a');
  link.download = 'suitability-checker.png';
  link.href = canvas.toDataURL('image/png');
  link.click();
};
